import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { APIPath, VERB } from '@ariksa/ai-engine';
import { Box, Center, HStack, useDisclosure } from '@chakra-ui/react';
import { isArray, isEmpty, map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { CustomTooltip, renderTime } from 'components/DataDisplay';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { OrganizationUnitIcon } from 'components/Icons';
import { SyncRestartIcon } from 'components/Icons/ReactCustomIcons/SyncRestartIcon';
import { AIServiceIcon, PrimaryButton } from 'components/index';
import { SecondaryNavBarRightComponent } from 'components/Navigation/NavBar/SecondaryNavBar/SecondaryNavBarRightComponent';
import { useMyTasks } from 'components/Navigation/NavBar/TopNavBar/RightComponent/MyTasks/useMyTasks';
import { useAlertNotification } from 'containers/App/hooks/notification/useAlertNotification';
import { useUserPermission } from 'containers/App/hooks/permissions/useUserPermission';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useApp } from 'containers/App/hooks/useApp';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { useKeycloakContext } from 'containers/App/keycloak/context';
import { NavBarWrapper } from 'containers/App/NavBarWrapper';
import { selectApp } from 'containers/App/selectors';
import { actions } from 'containers/App/slice';
import { useScannerInfo } from 'containers/SharedState/hooks/useScannerInfo';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';

import { colorOpacity, customTheme } from '../../../theme';

interface IAppNav {
  isOpenWelcomeScreen: boolean;

  onOpenCloudInsight(): void;
}

export const AppNav: FC<IAppNav> = props => {
  const { onOpenCloudInsight } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const app = useSelector(selectApp);
  const { cloudAccounts, user, environments } = app;
  const {
    lastRefreshed,
    faq,
    aiParamExtraction,
    journey,
    searchTerm,
  } = useSelector(selectSharedState);

  const aiSearchModal = useDisclosure();

  const { environmentId, accountId } = useAccessBoundary();

  const { resetUserPermission } = useUserPermission();
  const { logOut, resetPassword } = useKeycloakContext();

  const [faqSearchTerm, setFaqSearchTerm] = useState('');
  useAlertNotification();

  const { getMyTasks, getAllTasksStatus } = useMyTasks();
  const { updateValue } = useSearchParamFunctions();

  const { isMasterOrg } = useApp();
  const {
    onSuccessDataScanner,
    onSuccessVulnerabilityScanner,
  } = useScannerInfo();

  /*load the cloud accounts*/
  useEffect(() => {
    if (user.authenticated && !isMasterOrg) {
      dispatch(actions.loadResourceTypes({}));
      dispatch(actions.loadEnvAndAccounts());
      dispatch(sharedStateActions.getBlueprints({ q: {} }));
    }
  }, [dispatch, user.authenticated, isMasterOrg]);

  useEffect(() => {
    if (user.authenticated && !isMasterOrg) {
      dispatch(
        sharedStateActions.getDataScannerInfo({
          q: {},
          onSuccess: onSuccessDataScanner,
        }),
      );
      dispatch(
        sharedStateActions.getCentralVulnerabilityScanners({
          onSuccess: res => {
            if (!isEmpty(res)) onSuccessVulnerabilityScanner(res);
            else
              dispatch(
                sharedStateActions.getVulnerabilityScannerInfo({
                  q: {},
                  onSuccess: res => {
                    onSuccessVulnerabilityScanner(res);
                  },
                }),
              );
          },
        }),
      );
    }
  }, [
    dispatch,
    user.authenticated,
    isMasterOrg,
    onSuccessVulnerabilityScanner,
    onSuccessDataScanner,
  ]);

  const loadLastRefreshed = useCallback(() => {
    dispatch(
      sharedStateActions.getLastDiscoveryTime({
        q: {
          getLatestWorkflowRequest: {
            environment_id: environmentId,
            account_ids: accountId ? [accountId] : [],
          },
        },
      }),
    );
  }, [dispatch, environmentId, accountId]);

  useEffect(() => {
    !isMasterOrg && getMyTasks();
  }, [getMyTasks, isMasterOrg]);

  useEffect(() => {
    if (isMasterOrg) return;
    loadLastRefreshed();
    const interval = setInterval(() => {
      loadLastRefreshed();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [loadLastRefreshed, isMasterOrg]);

  useEffect(() => {
    !isMasterOrg &&
      dispatch(
        sharedStateActions.getFAQ({ q: { searchQuery: faqSearchTerm } }),
      );
  }, [dispatch, faqSearchTerm, isMasterOrg]);

  const handleLogOut = e => {
    e.preventDefault();
    e.stopPropagation();
    resetUserPermission();
    logOut();
    return false;
  };

  const onSelectEnvironmentOrAccount = useCallback(
    selected => {
      dispatch(actions.setAccount(selected));
    },
    [dispatch],
  );

  const filteredEnvironments = useMemo(() => {
    if (!isArray(environments.data)) return [];
    return environments.data.filter(env => {
      return Array.from(env.account_ids ?? []).some(accountId =>
        cloudAccounts.data.some(account => account.uuid === accountId),
      );
    });
  }, [cloudAccounts.data, environments.data]);

  // accounts the user have access to
  const filteredAccounts = useMemo(() => {
    const accountIds = user.info.account_ids;
    return cloudAccounts.data.filter(account => {
      return accountIds?.includes(account.uuid);
    });
  }, [cloudAccounts.data, user.info.account_ids]);

  const onClearSearchTerm = () => {
    dispatch(sharedStateActions.updateSearchTerm(''));
    dispatch(sharedStateActions.updateFinalSearchTerm(''));
    dispatch(sharedStateActions.resetAIParamExtraction());
    updateValue('search', '', 'search');
    updateValue('search_term', '', 'search_term');
  };

  return (
    <>
      <SecondaryNavBarRightComponent.Source>
        <HStack align="flex-start">
          <PrimaryButton
            leftIcon={<AIServiceIcon />}
            borderRadius={'full'}
            onClick={() => navigate('/home/ai-assistant')}
          >
            Ask Ariksa AI
          </PrimaryButton>
          <Box
            bg={colorOpacity(customTheme.colors.green['300'], 0.2)}
            py={0.5}
            borderRadius={6}
            px={2}
            color={customTheme.colors.green['300']}
          >
            <HStack>
              <Box boxSize={4}>
                <Center>
                  <OrganizationUnitIcon />
                </Center>
              </Box>
              <Box>{user.info.org_id}</Box>
            </HStack>
          </Box>
          <CustomTooltip
            label={
              <HStack fontSize="sm" h="full">
                <Box>Last Sync:</Box>
                <Box>
                  {lastRefreshed.isLoading ? (
                    <CustomSpinner loading={lastRefreshed.isLoading} />
                  ) : (
                    renderTime({ value: lastRefreshed.data })
                  )}
                </Box>
              </HStack>
            }
          >
            <Center h="full" color={customTheme.colors.selectIcon}>
              <Center boxSize={6}>
                <SyncRestartIcon />
              </Center>
            </Center>
          </CustomTooltip>
        </HStack>
      </SecondaryNavBarRightComponent.Source>
      {user.authenticated && user.info.id && (
        <NavBarWrapper
          showWelcomeScreen={props.isOpenWelcomeScreen}
          environments={{
            loading: environments.isLoading,
            loaded: environments.isSuccess,
            data: filteredEnvironments,
          }}
          accounts={{
            loading: cloudAccounts.loading,
            loaded: cloudAccounts.loaded,
            data: filteredAccounts,
          }}
          alerts={[]}
          onLogout={handleLogOut}
          resetPassword={resetPassword}
          user={user}
          onSelectEnvironmentOrAccount={onSelectEnvironmentOrAccount}
          faq={{
            data: faq.data,
            isLoading: faq.isLoading,
            searchTerm: faqSearchTerm,
            setSearchTerm: setFaqSearchTerm,
          }}
          journey={{
            myTasks: map(journey.myTasks.data, o => o),
            taskRead: journey.taskStatus,
            isLoadingMyTasks: journey.myTasks.isLoading,
            getMyTasksStatus: getAllTasksStatus,
          }}
          search={{
            value: searchTerm,
            aiSearchModal: aiSearchModal,
            onAiSearch: searchTerm => {
              console.log('SearchTerm', searchTerm);

              dispatch(sharedStateActions.updateSearchTerm(searchTerm));
              dispatch(
                sharedStateActions.extractParametersForSearch({
                  q: {
                    q: searchTerm,
                    apiPath: APIPath.NotificationAlertsAggregatedAlerts,
                    verb: VERB.Get,
                  },
                  onSuccess: () => {
                    aiSearchModal.onClose();
                  },
                  onError: () => {
                    aiSearchModal.onClose();
                  },
                }),
              );
            },
            onSearch: searchTerm => {
              dispatch(sharedStateActions.updateSearchTerm(searchTerm));
              dispatch(sharedStateActions.updateFinalSearchTerm(searchTerm));
              aiSearchModal.onClose();

              if (!searchTerm) onClearSearchTerm();
            },
            onClearSearch: onClearSearchTerm,
            onChangeSearchTerm: value => {
              dispatch(sharedStateActions.updateSearchTerm(value));
              if (!value) onClearSearchTerm();
            },
            isLoading: aiParamExtraction.isLoading,
          }}
          cloudInsight={{
            onOpen: onOpenCloudInsight,
          }}
        />
      )}
    </>
  );
};
